import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  FormGroup,
  Label,
  FormText,
  Row,
  Input,
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import useVendorBranch from '../../utils/useVendorBranch';
import { toast } from 'react-toastify';
import Select from 'react-select';

// ✅ NEW helper import (single source of truth)
import {
  gradeOptions,
  gradeToOption,
  normalizeGrade,
} from '../../utils/gradeHelper';

const EVALUATED_BY = {
  MARKS: 'MARKS',
  GRADE: 'GRADE',
};

const evaluatedByOptions = [
  { value: EVALUATED_BY.MARKS, label: 'Marks' },
  { value: EVALUATED_BY.GRADE, label: 'Grade' },
];

const toNumOrNull = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const AddEditCourseExamPattern = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch } = useVendorBranch(user);

  const [formData, setFormData] = useState({
    id: 0,
    vendor_id: 0,
    branch_id: 0,
    course_id: '',
    course_name: '',
    exam_term_id: '',
    term_name: '',

    evaluated_by: EVALUATED_BY.MARKS,
    passing_grade: '', // ✅ grade string, stored in DB (A+, A, ...)

    total_marks: '',
    passing_marks: '',
    weightage_percet: '',
  });

  const [errorMessage, setErrorMessage] = useState({});
  const [classes, setClasses] = useState([]);
  const [termOptions, setTermOptions] = useState([]);

  // ✅ Edit mode
  useEffect(() => {
    if (!location.state) return;

    const st = location.state || {};
    const evalByRaw =
      st.evaluated_by ||
      st.evaluatedBy ||
      st.Evaluated_By ||
      EVALUATED_BY.MARKS;

    const normalizedEval = String(evalByRaw || '')
      .toUpperCase()
      .includes('GRADE')
      ? EVALUATED_BY.GRADE
      : EVALUATED_BY.MARKS;

    setFormData((prev) => ({
      ...prev,
      ...st,
      evaluated_by: normalizedEval,
      passing_grade: normalizeGrade(
        st.passing_grade ??
          st.passingGrade ??
          st.Passing_Grade ??
          prev.passing_grade,
      ),
    }));
  }, [location.state]);

  const isGradeMode = useMemo(
    () =>
      String(formData.evaluated_by || '').toUpperCase() === EVALUATED_BY.GRADE,
    [formData.evaluated_by],
  );

  const validate = () => {
    setErrorMessage({});

    if (isSuperAdmin && !formData.vendor_id) {
      toast.error('Please select vendor');
      return false;
    }
    if ((isSuperAdmin || isAdmin || hasMultiBranch) && !formData.branch_id) {
      toast.error('Please select branch');
      return false;
    }

    if (!formData.course_id || Number(formData.course_id) <= 0) {
      setErrorMessage({ course_id: 'Course required' });
      return false;
    }
    if (!formData.course_name) {
      setErrorMessage({ course_id: 'Please select course properly' });
      return false;
    }

    if (!formData.exam_term_id || !formData.term_name) {
      setErrorMessage({ term_name: 'Term name required' });
      return false;
    }

    if (!formData.evaluated_by) {
      setErrorMessage({ evaluated_by: 'Evaluated By required' });
      return false;
    }

    // ✅ Grade mode: must pick from dropdown
    if (isGradeMode) {
      const g = normalizeGrade(formData.passing_grade);
      if (!g) {
        setErrorMessage({ passing_grade: 'Passing grade required' });
        return false;
      }
      return true;
    }

    // ✅ Marks mode
    const total = Number(formData.total_marks || 0);
    const pass = Number(formData.passing_marks || 0);

    if (!total || total <= 0) {
      setErrorMessage({ total_marks: 'Total marks required (> 0)' });
      return false;
    }
    if (pass < 0 || pass > total) {
      setErrorMessage({ passing_marks: 'Passing must be ≤ Total' });
      return false;
    }

    return true;
  };

  const fetchClasses = async (searchText = '') => {
    try {
      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${
          formData.vendor_id
        }&branchId=${formData.branch_id}&searchItem=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const raw = res?.data || [];
        setClasses(
          raw.map((x) => ({
            value: x.id,
            label: x.class_Name ?? x.class_display_name ?? 'No Name',
          })),
        );
      } else toast.error(res?.message || 'Failed to fetch courses');
    } catch (err) {
      console.log(err);
      toast.error('Failed to load courses');
    }
  };

  const getExamTermList = async (searchText = '') => {
    try {
      const res = await apiService.get(
        `api/ExamMaster/GetAllExamTerm?vendorId=${
          formData.vendor_id || ''
        }&branchId=${formData.branch_id || ''}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status) {
        const raw = res?.data || [];
        setTermOptions(
          raw.map((x) => ({
            value: x.id,
            label: x.term_name ?? x.termName ?? 'No Name',
          })),
        );
      } else toast.error(res?.message || 'Failed to load terms');
    } catch (e) {
      console.log(e);
      toast.error('Something went wrong');
    }
  };

  useEffect(() => {
    if (
      isSuperAdmin &&
      (!formData.vendor_id || Number(formData.vendor_id) <= 0)
    )
      return;
    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!formData.branch_id || Number(formData.branch_id) <= 0)
    )
      return;

    fetchClasses('');
    getExamTermList('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.vendor_id, formData.branch_id]);

  const onCourseSelectChange = (opt) => {
    setFormData((prev) => ({
      ...prev,
      course_id: opt?.value ?? 0,
      course_name: opt?.label ?? '',
    }));
    setErrorMessage((prev) => ({ ...prev, course_id: '' }));
  };

  const onTermSelectChange = (opt) => {
    setFormData((prev) => ({
      ...prev,
      exam_term_id: opt?.value ?? '',
      term_name: opt?.label ?? '',
    }));
    setErrorMessage((prev) => ({ ...prev, term_name: '' }));
  };

  const onEvaluatedByChange = (opt) => {
    const val = opt?.value || EVALUATED_BY.MARKS;

    setFormData((prev) => {
      if (val === EVALUATED_BY.GRADE) {
        return {
          ...prev,
          evaluated_by: val,
          passing_grade: normalizeGrade(prev.passing_grade),
          total_marks: '',
          passing_marks: '',
          weightage_percet: '',
        };
      }
      return {
        ...prev,
        evaluated_by: val,
        passing_grade: '',
      };
    });

    setErrorMessage((prev) => ({
      ...prev,
      evaluated_by: '',
      passing_grade: '',
      total_marks: '',
      passing_marks: '',
    }));
  };

  // ✅ Passing Grade dropdown change
  const onPassingGradeChange = (opt) => {
    setFormData((prev) => ({
      ...prev,
      passing_grade: opt?.value ?? '',
    }));
    setErrorMessage((prev) => ({ ...prev, passing_grade: '' }));
  };

  const saveData = async () => {
    if (!validate()) return;

    try {
      const payload = {
        ...formData,
        course_id: formData.course_id ? parseInt(formData.course_id, 10) : 0,
        course_name: formData.course_name || '',
        exam_term_id: formData.exam_term_id
          ? parseInt(formData.exam_term_id, 10)
          : 0,
        term_name: formData.term_name || '',

        evaluated_by: formData.evaluated_by || EVALUATED_BY.MARKS,

        // ✅ grade string (dropdown), else null
        passing_grade: isGradeMode
          ? normalizeGrade(formData.passing_grade)
          : null,

        // ✅ marks else null
        total_marks: !isGradeMode ? Number(formData.total_marks || 0) : null,
        passing_marks: !isGradeMode
          ? Number(formData.passing_marks || 0)
          : null,
        weightage_percet: !isGradeMode
          ? toNumOrNull(formData.weightage_percet)
          : null,
      };

      let res;
      if (formData.id > 0) {
        res = await apiService.put(
          `api/ExamMaster/UpdateCourseExamPattern?id=${formData.id}`,
          payload,
        );
      } else {
        res = await apiService.post(
          'api/ExamMaster/AddCourseExamPattern',
          payload,
        );
      }

      if (res?.status) {
        toast.success(res.message);
        navigate('/list-course-exam-pattern');
      } else toast.error(res?.message || 'Save failed');
    } catch {
      toast.error('Something went wrong');
    }
  };

  const deleteData = async () => {
    if (!window.confirm('Delete this record?')) return;

    const res = await apiService.delete(
      `api/ExamMaster/DeleteCourseExamPattern?id=${formData.id}`,
    );

    if (res?.status) {
      toast.success(res.message);
      navigate('/list-course-exam-pattern');
    } else toast.error(res?.message || 'Delete failed');
  };

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={formData}
        setSelectionData={setFormData}
        errorMessage={errorMessage}
        accessButton
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='border-bottom p-3 d-flex justify-content-between'>
          <span>Course Exam Pattern</span>
          <div>
            <Button color='warning' onClick={() => navigate(-1)}>
              <i className='bi bi-arrow-left' />
            </Button>

            {formData.id > 0 && (
              <Button color='danger' className='ml-2' onClick={deleteData}>
                <i className='bi bi-trash' />
              </Button>
            )}
          </div>
        </CardTitle>

        <CardBody>
          <Row>
            <FormGroup className='col-sm-4'>
              <Label>Course</Label>
              <Select
                options={classes}
                value={classes.find(
                  (o) => String(o.value) === String(formData.course_id),
                )}
                onChange={onCourseSelectChange}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === 'input-change')
                    fetchClasses(inputValue);
                }}
                isSearchable
                isClearable
                placeholder='Search courses ...'
              />
              <FormText color='danger'>{errorMessage.course_id}</FormText>
            </FormGroup>

            <FormGroup className='col-sm-4'>
              <Label>Term Name</Label>
              <Select
                options={termOptions}
                value={termOptions.find(
                  (o) => String(o.value) === String(formData.exam_term_id),
                )}
                onChange={onTermSelectChange}
                onInputChange={(inputValue, actionMeta) => {
                  if (actionMeta.action === 'input-change')
                    getExamTermList(inputValue);
                }}
                isSearchable
                isClearable
                placeholder='Search term name ...'
              />
              <FormText color='danger'>{errorMessage.term_name}</FormText>
            </FormGroup>

            <FormGroup className='col-sm-4'>
              <Label>Evaluated By</Label>
              <Select
                options={evaluatedByOptions}
                value={evaluatedByOptions.find(
                  (x) =>
                    x.value === (formData.evaluated_by || EVALUATED_BY.MARKS),
                )}
                onChange={onEvaluatedByChange}
                isSearchable={false}
                isClearable={false}
              />
              <FormText color='danger'>{errorMessage.evaluated_by}</FormText>
            </FormGroup>

            {isGradeMode ? (
              <FormGroup className='col-sm-4'>
                <Label>Passing Grade</Label>
                <Select
                  options={gradeOptions}
                  value={gradeToOption(formData.passing_grade)}
                  onChange={onPassingGradeChange}
                  isSearchable={false}
                  isClearable
                  placeholder='Select grade...'
                />
                <FormText color='danger'>{errorMessage.passing_grade}</FormText>
              </FormGroup>
            ) : (
              <>
                <FormGroup className='col-sm-4'>
                  <Label>Total Marks</Label>
                  <Input
                    type='number'
                    name='total_marks'
                    value={formData.total_marks}
                    onChange={(e) => {
                      setFormData((p) => ({
                        ...p,
                        total_marks: e.target.value,
                      }));
                      setErrorMessage((p) => ({ ...p, total_marks: '' }));
                    }}
                  />
                  <FormText color='danger'>{errorMessage.total_marks}</FormText>
                </FormGroup>

                <FormGroup className='col-sm-4'>
                  <Label>Passing Marks</Label>
                  <Input
                    type='number'
                    name='passing_marks'
                    value={formData.passing_marks}
                    onChange={(e) => {
                      setFormData((p) => ({
                        ...p,
                        passing_marks: e.target.value,
                      }));
                      setErrorMessage((p) => ({ ...p, passing_marks: '' }));
                    }}
                  />
                  <FormText color='danger'>
                    {errorMessage.passing_marks}
                  </FormText>
                </FormGroup>

                <FormGroup className='col-sm-4'>
                  <Label>Weightage %</Label>
                  <Input
                    type='number'
                    name='weightage_percet'
                    value={formData.weightage_percet}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        weightage_percet: e.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </>
            )}
          </Row>

          <Button color='primary' className='float-end' onClick={saveData}>
            Save
          </Button>
        </CardBody>
      </Card>
    </>
  );
};

export default AddEditCourseExamPattern;
